import { useEffect, useState } from "react";
import { api, fmtINR } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { TrendingUp, Layers, CheckCircle2, Clock, Ban, Landmark, Radar } from "lucide-react";
import { KiteExpiredBanner } from "@/components/KiteConnect";

const STATE = {
  BUY_SUBMITTED: { label: "BUY PENDING", color: "#FFB300" },
  HOLDING: { label: "HOLDING", color: "#007AFF" },
  TARGET_RESTING: { label: "TARGET SET", color: "#00E676" },
  EXIT_SUBMITTED: { label: "SELLING…", color: "#FF3B30" },
};

function daysHeld(boughtDate) {
  if (!boughtDate) return 0;
  const b = new Date(boughtDate + "T00:00:00");
  const now = new Date();
  return Math.max(0, Math.round((now - b) / 86400000));
}

// mirror of backend costs.py so we can preview net before a manual sell
function intradayCost(buy, sell) {
  const brokerage = Math.min(20, 0.0003 * buy) + Math.min(20, 0.0003 * sell);
  const stt = 0.00025 * sell;
  const exch = 0.0000297 * (buy + sell);
  const sebi = 0.000001 * (buy + sell);
  const stamp = 0.00003 * buy;
  const gst = 0.18 * (brokerage + exch + sebi);
  return brokerage + stt + exch + sebi + stamp + gst;
}
function deliveryCost(buy, sell) {
  const stt = 0.001 * (buy + sell);
  const exch = 0.0000297 * (buy + sell);
  const sebi = 0.000001 * (buy + sell);
  const stamp = 0.00015 * buy;
  const gst = 0.18 * (exch + sebi);
  return stt + exch + sebi + stamp + gst + 15.93;
}
function sellEstimate(pos, today) {
  const fill = pos.ltp;                                       // marketable limit fills ≈ at LTP (market)
  const limitPrice = Math.round(pos.ltp * 0.995 * 10) / 10;   // protective limit price actually sent
  const qty = pos.qty;
  const buyVal = pos.entry_price * qty;
  const sellVal = fill * qty;
  const sameDay = pos.bought_date === today;
  const cost = sameDay ? intradayCost(buyVal, sellVal) : deliveryCost(buyVal, sellVal);
  const gross = sellVal - buyVal;
  return { fill, limitPrice, qty, gross, cost, net: gross - cost, basis: sameDay ? "Intraday" : "Delivery" };
}

function Stat({ label, value, color, testid }) {
  return (
    <div className="bg-elevated border border-edge rounded-sm px-2.5 py-2" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.12em] text-muted2">{label}</div>
      <div className="mono text-sm font-bold mt-0.5" style={{ color: color || "#fff" }}>{value}</div>
    </div>
  );
}

export default function Swing() {
  const [d, setD] = useState(null);
  const [status, setStatus] = useState(null);
  const [err, setErr] = useState(null);
  const [confirm, setConfirm] = useState(null); // {symbol, kind:'sell'|'cancel'}
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.swing().then((r) => { setD(r); setErr(null); }).catch((e) => setErr(e?.response?.data?.detail || e?.message || "Could not load swing data"));
    api.status().then(setStatus).catch(() => {});
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, []);

  const doExit = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      const res = confirm.kind === "demat"
        ? await api.swingSellDemat(confirm.symbol)
        : confirm.kind === "release"
        ? await api.swingReleaseDemat(confirm.symbol)
        : await api.swingExit(confirm.symbol);
      toast.success(res.message || "Done");
      load();
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || "Try again" });
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  if (!d) return (
    <div className="p-6 text-sm" data-testid="swing-loading">
      {err ? (
        <div className="bg-neg/10 border border-neg/30 rounded-sm px-4 py-4 text-neg" data-testid="swing-load-error">
          <div className="font-bold mb-1">Couldn't load the Swing dashboard</div>
          <div className="text-[11px] text-neg/80 mono mb-3 break-words">{String(err)}</div>
          <button onClick={load} className="px-3 py-1.5 rounded-sm text-xs font-bold border border-neg/40 bg-neg/10 text-neg" data-testid="swing-retry-btn">Retry</button>
          <div className="text-[10px] text-muted2 mt-2">Retrying automatically every 4s…</div>
        </div>
      ) : (
        <span className="text-muted2">Loading swing dashboard…</span>
      )}
    </div>
  );

  const positions = d.positions || [];
  const closed = d.closed || [];
  const released = d.released || [];
  const unreal = positions.reduce((s, p) => s + (p.pnl || 0), 0);
  const realized = d.closed_net_pnl || 0;
  const wr = d.closed_count ? Math.round((d.closed_wins / d.closed_count) * 100) : 0;
  const deployedPct = d.capital_ceiling ? Math.min(100, (d.deployed_capital / d.capital_ceiling) * 100) : 0;
  const isSwing = status?.strategy === "SWING";
  const isLive = status?.mode === "LIVE";
  const killed = status?.killed;
  const today = status?.market?.ist_date;

  return (
    <div className="pb-40" data-testid="swing-dashboard">
      <div className="glass sticky top-0 z-30 border-b border-edge px-4 py-3 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
            <TrendingUp size={18} className="text-pos" /> Swing Bot
          </h1>
          <p className="text-[11px] text-muted2">{d.stop_pct > 0
            ? `Autonomous CNC · +${d.entry_pct}% in · ${d.stop_pct}% trailing exit (let winners run)`
            : `Autonomous CNC · +${d.entry_pct}% in / +${d.target_pct}% out · no stop`}</p>
        </div>
        <span
          className={`mono text-[10px] font-bold px-2 py-1 rounded-sm border ${
            killed ? "text-kill border-kill/40 bg-kill/10"
            : isSwing && isLive ? "text-pos border-pos/40 bg-pos/10"
            : "text-muted2 border-edge bg-elevated"
          }`}
          data-testid="swing-status-pill"
        >
          {killed ? "KILLED" : isSwing && isLive ? "LIVE" : isSwing ? "PAPER" : "OFF"}
        </span>
      </div>

      <div className="p-4 space-y-4">
        <KiteExpiredBanner show={status?.kite_auth_expired} />
        {isSwing && d.scan_status && (
          <div className="bg-brand/10 border border-brand/30 rounded-sm px-3 py-2.5 flex items-start gap-2 text-[11px] text-brand" data-testid="swing-scan-status">
            <Radar size={14} className="mt-0.5 shrink-0" />
            <span className="leading-snug"><span className="font-bold">Bot status:</span> {d.scan_status}</span>
          </div>
        )}
        {isSwing && d.scan_funnel?.stages?.length > 0 && (
          <div className="bg-surface border border-brand/20 rounded-sm px-3 py-2.5" data-testid="swing-scan-funnel">
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted2 mb-2">
              Last scan funnel{d.scan_funnel.ts ? ` · ${new Date(d.scan_funnel.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {d.scan_funnel.stages.map((s, i) => (
                <span key={i} className="flex items-center gap-1.5">
                  <span
                    className={`px-2 py-1 rounded-sm text-[11px] font-mono tabular-nums ${s.count === 0 ? "bg-neg/15 text-neg border border-neg/30" : "bg-pos/10 text-pos border border-pos/25"}`}
                    data-testid={`funnel-stage-${i}`}
                  >
                    <span className="font-bold">{s.count}</span> {s.label}
                  </span>
                  {i < d.scan_funnel.stages.length - 1 && <span className="text-muted2">→</span>}
                </span>
              ))}
            </div>
            <div className="text-[10px] text-muted2 mt-2 leading-snug">
              The first stage that drops to <span className="text-neg font-semibold">0</span> is what's blocking buys — loosen that filter in Config.
            </div>
          </div>
        )}
        {!isSwing && (
          <div className="bg-warn/10 border border-warn/30 rounded-sm px-3 py-2.5 flex items-center gap-2 text-[11px] text-warn" data-testid="swing-off-note">
            <Ban size={14} /> Strategy is not set to SWING. Enable it in Config → Strategy to run the bot.
          </div>
        )}

        {/* capital gauge */}
        <div className="bg-surface border border-pos/25 rounded-sm p-3" data-testid="swing-capital-card">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted2">Capital Deployed</span>
            <span className="mono text-xs font-bold text-white">{fmtINR(d.deployed_capital)} <span className="text-muted2">/ {fmtINR(d.capital_ceiling)}</span></span>
          </div>
          <div className="h-2 w-full bg-elevated rounded-full overflow-hidden">
            <div className="h-full bg-pos transition-all duration-500" style={{ width: `${deployedPct}%` }} />
          </div>
          <div className="mt-1 text-[10px] text-muted2 mono">{deployedPct.toFixed(0)}% used · ₹{d.notional_per_order}/order</div>
        </div>

        {/* summary stats */}
        <div className="grid grid-cols-3 gap-2">
          <Stat label="Buys Today" value={`${d.new_buys_today}`} testid="swing-stat-buys" />
          <Stat label="Open" value={`${positions.length}/${d.max_positions ?? "—"}`} testid="swing-stat-open" />
          <Stat label="Unrealized" value={fmtINR(unreal)} color={unreal >= 0 ? "#00E676" : "#FF3B30"} testid="swing-stat-unreal" />
          <Stat label="Realized" value={fmtINR(realized)} color={realized >= 0 ? "#00E676" : "#FF3B30"} testid="swing-stat-realized" />
          <Stat label="Closed" value={d.closed_count || 0} testid="swing-stat-closed" />
          <Stat label="Win Rate" value={`${wr}%`} color={wr >= 50 ? "#00E676" : "#FFB300"} testid="swing-stat-wr" />
        </div>

        {/* active holdings */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Layers size={14} className="text-brand" />
            <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-muted2">Active Holdings ({positions.length})</h2>
          </div>
          <div className="grid grid-cols-1 gap-2">
            {positions.map((p) => {
              const st = STATE[p.state] || { label: p.state, color: "#8A919E" };
              const pos = p.pnl >= 0;
              const held = daysHeld(p.bought_date);
              return (
                <div key={p.symbol} className="bg-surface border border-edge rounded-sm px-3 py-2.5" data-testid={`swing-holding-${p.symbol}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-white">{p.symbol}</span>
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm" style={{ color: st.color, background: st.color + "22" }}>{st.label}</span>
                      {p.circuit_pct != null && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm bg-elevated border border-edge text-muted2" data-testid={`swing-band-${p.symbol}`}>
                          {p.circuit_pct}% band
                        </span>
                      )}
                    </div>
                    <div className="text-right">
                      <div className={`mono text-sm font-bold ${pos ? "text-pos" : "text-neg"}`}>{fmtINR(p.pnl)}</div>
                      <div className={`mono text-[10px] ${pos ? "text-pos" : "text-neg"}`}>{p.pnl_pct >= 0 ? "+" : ""}{p.pnl_pct}%</div>
                    </div>
                  </div>
                  <div className="mono text-[10px] text-muted2 mt-1.5 flex items-center gap-3 flex-wrap">
                    <span>{p.qty} sh</span>
                    <span>entry ₹{p.entry_price}</span>
                    <span>ltp ₹{p.ltp}</span>
                    {p.target_price != null && <span className="text-pos">tgt ₹{p.target_price}</span>}
                    {p.stop_price != null && (
                      <span className={p.stop_locked ? "text-pos" : "text-neg"}>
                        {p.stop_locked ? "b/e-lock ₹" : "trail ₹"}{p.stop_price}
                      </span>
                    )}
                    <span className="flex items-center gap-1"><Clock size={10} /> {held}d · {p.sessions_held ?? 0} sess</span>
                    {p.buyer_pct != null && <span className={p.buyer_pct >= 70 ? "text-pos" : "text-muted2"}>buyers {p.buyer_pct}%</span>}
                    {p.month_change_pct != null && <span className={p.month_change_pct >= 0 ? "text-pos" : "text-neg"}>1M {p.month_change_pct >= 0 ? "+" : ""}{p.month_change_pct}%</span>}
                    {p.sessions_to_release === 0
                      ? <span className="text-brand font-bold" data-testid={`swing-release-due-${p.symbol}`}>→ demat next scan</span>
                      : <span className="text-muted2">→ demat in {p.sessions_to_release} sess</span>}
                  </div>
                  <HoldingAction pos={p} today={today} isLive={isLive} onAct={setConfirm} />
                </div>
              );
            })}
            {positions.length === 0 && (
              <div className="border border-dashed border-edge rounded-sm py-8 text-center text-xs text-muted2" data-testid="swing-no-holdings">
                No active holdings. Bot buys when a stock crosses +{d.entry_pct}% intraday.
              </div>
            )}
          </div>
        </div>

        {/* closed trades */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 size={14} className="text-pos" />
            <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-muted2">Closed Trades · today ({closed.length})</h2>
          </div>
          <div className="border border-edge rounded-sm overflow-hidden bg-surface divide-y divide-edge/50" data-testid="swing-closed-list">
            {closed.map((t, i) => {
              const net = t.net_pnl || 0;
              const win = net >= 0;
              return (
                <div key={i} className="px-3 py-2.5" data-testid={`swing-closed-${t.symbol}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-bold text-xs text-white flex items-center gap-1.5">
                      {t.symbol}
                      {t.cost_basis && (
                        <span className="text-[8px] font-bold px-1 py-0.5 rounded-sm bg-elevated text-muted2 border border-edge">
                          {t.cost_basis === "INTRADAY" ? "INTRA" : "DELIVERY"}
                        </span>
                      )}
                    </div>
                    <div className={`mono text-xs font-bold ${win ? "text-pos" : "text-neg"}`}>{win ? "✓" : "✗"} Net {fmtINR(net)}</div>
                  </div>
                  <div className="mono text-[10px] text-muted2 mt-0.5">
                    {t.qty} · ₹{t.avg_price} → ₹{t.exit_price}
                  </div>
                  <div className="mt-1.5 grid grid-cols-3 gap-1.5">
                    <div className="bg-elevated border border-edge rounded-sm px-2 py-1" data-testid={`swing-closed-gross-${t.symbol}`}>
                      <div className="text-[8px] uppercase tracking-wide text-muted2">Gross</div>
                      <div className={`mono text-[11px] font-bold ${(t.gross_pnl || 0) >= 0 ? "text-pos" : "text-neg"}`}>{fmtINR(t.gross_pnl || 0)}</div>
                    </div>
                    <div className="bg-elevated border border-edge rounded-sm px-2 py-1" data-testid={`swing-closed-cost-${t.symbol}`}>
                      <div className="text-[8px] uppercase tracking-wide text-muted2">Charges</div>
                      <div className="mono text-[11px] font-bold text-warn">-{fmtINR(t.costs || 0)}</div>
                    </div>
                    <div className="bg-elevated border border-edge rounded-sm px-2 py-1" data-testid={`swing-closed-net-${t.symbol}`}>
                      <div className="text-[8px] uppercase tracking-wide text-muted2">Net</div>
                      <div className={`mono text-[11px] font-bold ${win ? "text-pos" : "text-neg"}`}>{fmtINR(net)}</div>
                    </div>
                  </div>
                </div>
              );
            })}
            {closed.length === 0 && (
              <div className="px-3 py-8 text-center text-xs text-muted2" data-testid="swing-no-closed">
                No closed trades yet. Targets fill at +{d.target_pct}% from your buy price.
              </div>
            )}
          </div>
        </div>

        {/* released to demat */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Landmark size={14} className="text-brand" />
            <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-muted2">Released to Demat ({released.length})</h2>
          </div>
          <div className="border border-edge rounded-sm overflow-hidden bg-surface divide-y divide-edge/50" data-testid="swing-released-list">
            {released.map((r) => {
              const qty = r.filled_qty || r.qty;
              const invested = (r.avg_price || r.entry_limit || 0) * qty;
              const hasLtp = r.ltp != null;
              const up = (r.unrealized || 0) >= 0;
              return (
                <div key={r.symbol} className="px-3 py-2.5 flex items-center justify-between gap-2" data-testid={`swing-released-${r.symbol}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-xs text-white">{r.symbol}</span>
                      {hasLtp && (
                        <span className={`mono text-[10px] font-bold ${up ? "text-pos" : "text-neg"}`} data-testid={`swing-released-pnl-${r.symbol}`}>
                          {up ? "+" : ""}{fmtINR(r.unrealized)} ({up ? "+" : ""}{r.unrealized_pct}%)
                        </span>
                      )}
                    </div>
                    <div className="mono text-[10px] text-muted2 mt-0.5">
                      {qty} sh · avg ₹{r.avg_price ?? r.entry_limit}{hasLtp ? ` · LTP ₹${r.ltp}` : " · LTP —"} · inv ₹{fmtINR(invested, 0).replace("₹", "")}
                    </div>
                    <div className="mono text-[9px] text-muted2 mt-0.5">
                      held {r.released_after_sessions ?? "7+"} sessions · in your demat
                    </div>
                  </div>
                  <button
                    onClick={() => setConfirm({ symbol: r.symbol, kind: "demat" })}
                    disabled={!isLive}
                    className="shrink-0 text-[10px] font-bold px-3 py-2 rounded-sm bg-neg/90 text-white disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`swing-sell-demat-${r.symbol}`}
                  >
                    SELL
                  </button>
                </div>
              );
            })}
            {released.length === 0 && (
              <div className="px-3 py-8 text-center text-xs text-muted2" data-testid="swing-no-released">
                Nothing released yet. Holdings older than {d.release_sessions || 7} trading sessions move here (kept in your demat) and free a slot.
              </div>
            )}
          </div>
        </div>
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-surface border-edge max-w-[340px]" data-testid="swing-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              {confirm?.kind === "cancel" ? `Cancel buy — ${confirm?.symbol}?`
                : confirm?.kind === "demat" ? `Sell demat holding — ${confirm?.symbol}?`
                : confirm?.kind === "release" ? `Move ${confirm?.symbol} to demat?`
                : `Sell ${confirm?.symbol} now?`}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted2 text-xs">
              {confirm?.kind === "cancel"
                ? "This cancels the pending CNC buy order. No shares will be purchased."
                : confirm?.kind === "demat"
                ? "This places a REAL delivery (CNC) sell for your full demat holding at a marketable limit (~current market price). Charges are booked as delivery."
                : confirm?.kind === "release"
                ? "Keeps the shares in your demat but STOPS the bot managing them — cancels any resting exit/target and frees the slot. Nothing is sold. Use this to hold a loser instead of letting the trailing stop sell it. You can still sell it later from the Released to Demat list."
                : "This places a REAL sell for the full holding (marketable limit) and cancels any resting exit order."}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {confirm?.kind === "sell" && confirm?.pos && (() => {
            const e = sellEstimate(confirm.pos, confirm.today);
            return (
              <div className="rounded-sm border border-edge bg-elevated p-2.5 text-xs" data-testid="swing-sell-estimate">
                <div className="flex justify-between py-0.5"><span className="text-muted2">Est. fill ~</span><span className="mono text-white">₹{e.fill} × {e.qty}</span></div>
                <div className="flex justify-between py-0.5"><span className="text-muted2">Gross</span><span className={`mono ${e.gross >= 0 ? "text-pos" : "text-neg"}`}>{fmtINR(e.gross)}</span></div>
                <div className="flex justify-between py-0.5"><span className="text-muted2">Charges ({e.basis})</span><span className="mono text-warn">-{fmtINR(e.cost)}</span></div>
                <div className="flex justify-between py-0.5 border-t border-edge/60 mt-1 pt-1"><span className="text-white font-semibold">Est. Net</span><span className={`mono font-bold ${e.net >= 0 ? "text-pos" : "text-neg"}`}>{fmtINR(e.net)}</span></div>
                <div className="text-[9px] text-muted2 mt-1">Sent as a marketable limit ≈₹{e.limitPrice} to guarantee the fill; actual price ≈ market (LTP ₹{e.fill}).</div>
              </div>
            );
          })()}

          <AlertDialogFooter>
            <AlertDialogCancel className="bg-elevated border-edge text-white" data-testid="swing-confirm-cancel">Back</AlertDialogCancel>
            <AlertDialogAction
              onClick={doExit}
              disabled={busy}
              className={confirm?.kind === "cancel" ? "bg-warn text-black" : confirm?.kind === "release" ? "bg-brand text-white" : "bg-neg text-white"}
              data-testid="swing-confirm-action"
            >
              {busy ? "Working…" : confirm?.kind === "cancel" ? "Cancel Buy" : confirm?.kind === "demat" ? "Sell Demat Holding" : confirm?.kind === "release" ? "Move to Demat" : "Sell at Market"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function HoldingAction({ pos, today, isLive, onAct }) {
  if (pos.state === "EXIT_SUBMITTED") {
    return <div className="mt-2 text-[10px] font-bold text-neg" data-testid={`swing-selling-${pos.symbol}`}>Sell submitted — awaiting fill…</div>;
  }
  if (pos.state === "BUY_SUBMITTED") {
    return (
      <button
        onClick={() => onAct({ symbol: pos.symbol, kind: "cancel" })}
        disabled={!isLive}
        className="mt-2 w-full py-2 rounded-sm text-[11px] font-bold tracking-wider border border-warn/40 text-warn bg-warn/10 disabled:opacity-40"
        data-testid={`swing-cancel-${pos.symbol}`}
      >
        CANCEL BUY
      </button>
    );
  }
  // HOLDING / TARGET_RESTING — buy has filled, so a same-day sell is allowed (intraday-booked)
  return (
    <div className="mt-2 flex gap-2">
      <button
        onClick={() => onAct({ symbol: pos.symbol, kind: "sell", pos, today })}
        disabled={!isLive}
        className="flex-1 py-2 rounded-sm text-[11px] font-bold tracking-wider border border-neg/40 text-neg bg-neg/10 disabled:opacity-40"
        data-testid={`swing-sell-${pos.symbol}`}
      >
        SELL NOW · MARKET
      </button>
      <button
        onClick={() => onAct({ symbol: pos.symbol, kind: "release" })}
        disabled={!isLive}
        className="flex-1 py-2 rounded-sm text-[11px] font-bold tracking-wider border border-brand/40 text-brand bg-brand/10 disabled:opacity-40"
        data-testid={`swing-release-${pos.symbol}`}
      >
        KEEP · MOVE TO DEMAT
      </button>
    </div>
  );
}
